import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getProject, Project, getTasksForProject, Task, processPrompt } from '../services/api';

const ProjectWorkspace = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const [project, setProject] = useState<Project | null>(null);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [newPrompt, setNewPrompt] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [groundingEnabled, setGroundingEnabled] = useState<boolean>(false);

    const fetchProjectData = useCallback(async () => {
        if (!projectId) {
            setError("No project ID provided.");
            setLoading(false);
            return;
        }
        try {
            const projectData = await getProject(projectId);
            setProject(projectData);
            const tasksData = await getTasksForProject(projectId);
            setTasks(tasksData);
        } catch (err) {
            setError('Failed to fetch project data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchProjectData();
    }, [fetchProjectData]);

    const handlePromptSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPrompt || !projectId) return;

        setIsProcessing(true);
        setError(null);
        try {
            await processPrompt(projectId, newPrompt, groundingEnabled ? 'auto' : 'off');
            setNewPrompt('');
            // Refresh the task list after processing
            await fetchProjectData(); 
        } catch (err) {
            setError('Failed to process prompt.');
            console.error(err);
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) {
        return <div>Loading workspace...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    if (!project) {
        return <div>Project not found.</div>;
    }

    return (
        <div>
            <h2>{project.name}</h2>
            <p>{project.description}</p>
            <hr />
            
            <div>
                <h3>SEO Masterplan</h3>
                {project.masterplan_document ? (
                    <div dangerouslySetInnerHTML={{ __html: project.masterplan_document }} />
                ) : (
                    <p>No masterplan document uploaded.</p>
                )}
            </div>

            <hr />
            <div>
                <h3>New Task</h3>
                <form onSubmit={handlePromptSubmit}>
                    <textarea
                        value={newPrompt}
                        onChange={(e) => setNewPrompt(e.target.value)}
                        placeholder="Enter your prompt for the AI Manager..."
                        rows={4}
                        style={{ width: '100%', marginBottom: '10px' }}
                    />
                    <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            <input
                                type="checkbox"
                                checked={groundingEnabled}
                                onChange={e => setGroundingEnabled(e.target.checked)}
                                style={{ accentColor: 'var(--primary-500)' }}
                            />
                            Research live sources
                        </label>
                    </div>
                    <button type="submit" disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : 'Send Prompt'}
                    </button>
                </form>
            </div>

            <hr />
            <div>
                <h3>Tasks</h3>
                {tasks.length === 0 ? (
                    <p>No tasks yet for this project.</p>
                ) : (
                    <ul>
                        {tasks.map(task => (
                            <li key={task.task_id} style={{ marginBottom: '15px' }}>
                                <strong>{task.name}</strong>
                                <p>Status: {task.status}</p>
                                {task.synthesized_result && <p>Result: {task.synthesized_result}</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default ProjectWorkspace;